import React, { useEffect, useState } from 'react';
import { holidaysAPI, companiesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { CalendarDays, Plus, Trash2, Edit2, Loader2, Upload } from 'lucide-react';

const COUNTRIES = ['UAE', 'India', 'Ghana', 'Cameroon', 'Kenya', 'Mauritius', 'UK', 'Singapore', 'Uganda'];
const TYPES = [{ v: 'mandatory', l: 'Mandatory' }, { v: 'optional', l: 'Optional' }, { v: 'restricted', l: 'Restricted' }];

const Holidays = () => {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [dialog, setDialog] = useState({ open: false, edit: null });
  const [form, setForm] = useState({ name: '', date: '', country: '', company_id: '', type: 'mandatory', description: '' });
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (filterCountry !== 'all') params.country = filterCountry;
      if (filterType !== 'all') params.type = filterType;
      const [rRes, cRes] = await Promise.all([holidaysAPI.list(params), companiesAPI.getAll()]);
      setRows(rRes.data || []);
      setCompanies(cRes.data || []);
    } catch (e) { toast.error('Failed to load holidays'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [year, filterCountry, filterType]);

  const openCreate = () => { setForm({ name: '', date: `${year}-01-01`, country: '', company_id: '', type: 'mandatory', description: '' }); setDialog({ open: true, edit: null }); };
  const openEdit = (h) => { setForm({ name: h.name, date: h.date, country: h.country || '', company_id: h.company_id || '', type: h.type || 'mandatory', description: h.description || '' }); setDialog({ open: true, edit: h }); };

  const save = async () => {
    if (!form.name || !form.date) { toast.error('Name and date are required'); return; }
    setBusy(true);
    try {
      const payload = { ...form, country: form.country || null, company_id: form.company_id || null, description: form.description || null };
      if (dialog.edit) await holidaysAPI.update(dialog.edit.id, payload);
      else await holidaysAPI.create(payload);
      toast.success(dialog.edit ? 'Holiday updated' : 'Holiday added');
      setDialog({ open: false, edit: null });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}"?`)) return;
    try { await holidaysAPI.delete(h.id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const bulkImport = async () => {
    let list;
    try { list = JSON.parse(bulkText); } catch { toast.error('Invalid JSON'); return; }
    if (!Array.isArray(list) || list.length === 0) { toast.error('Provide a JSON array'); return; }
    try {
      const r = await holidaysAPI.bulkImport(list);
      toast.success(`Created ${r.data.created}, skipped ${r.data.skipped}`);
      setBulkOpen(false); setBulkText(''); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays size={22} />Holidays</h1>
          <p className="text-muted-foreground text-sm">Manage country- and company-specific holiday calendars.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="holidays-bulk-btn"><Upload size={14} className="mr-1" />Bulk Import</Button>
          <Button onClick={openCreate} data-testid="holidays-create-btn"><Plus size={14} className="mr-1" />Add Holiday</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div><Label>Year</Label><Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="holidays-year" /></div>
        <div><Label>Country</Label>
          <Select value={filterCountry} onValueChange={setFilterCountry}>
            <SelectTrigger className="w-40" data-testid="holidays-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36" data-testid="holidays-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No holidays found for the selected filters.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y" data-testid="holidays-list">
              {rows.map((h) => (
                <div key={h.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`holiday-row-${h.id}`}>
                  <CalendarDays size={16} className="text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{h.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {h.date} · {h.country || 'All countries'} · {h.company_id ? companies.find((c) => c.id === h.company_id)?.name || h.company_id : 'All companies'}
                    </div>
                  </div>
                  <Badge variant={h.type === 'mandatory' ? 'default' : (h.type === 'optional' ? 'success' : 'secondary')}>{h.type}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(h)} data-testid={`holiday-edit-${h.id}`}><Edit2 size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(h)} data-testid={`holiday-delete-${h.id}`}><Trash2 size={14} className="text-red-600" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog({ open: o, edit: dialog.edit })}>
        <DialogContent className="max-w-lg" data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.edit ? 'Edit holiday' : 'Add holiday'}</DialogTitle>
            <DialogDescription>Country and Company are optional. Leave both blank for a global holiday.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holiday-name" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="holiday-date" /></div>
            <div><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Country (optional)</Label>
              <Select value={form.country || 'none'} onValueChange={(v) => setForm({ ...form, country: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="holiday-country-select"><SelectValue placeholder="All countries" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All countries</SelectItem>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Company (optional)</Label>
              <Select value={form.company_id || 'none'} onValueChange={(v) => setForm({ ...form, company_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="holiday-company-select"><SelectValue placeholder="All companies" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">All companies</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, edit: null })}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="holiday-save">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-2xl" data-testid="holiday-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk import holidays</DialogTitle>
            <DialogDescription>Paste a JSON array of {`{name, date, country?, company_id?, type?}`}.</DialogDescription>
          </DialogHeader>
          <textarea className="w-full h-64 border rounded p-2 font-mono text-xs" value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder='[{"name":"Independence Day","date":"2026-08-15","country":"India","type":"mandatory"}]' data-testid="holiday-bulk-textarea" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={bulkImport} data-testid="holiday-bulk-import">Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Holidays;
